<template>
    <div class="qi-modal-backdrop" @click.self="$emit('close')">
        <div class="qi-modal-card">
            <div class="qi-modal-header">
                <h6 class="mb-0">Add "{{ initialName }}"</h6>
                <button type="button" class="btn btn-sm btn-link text-muted p-0" @click="$emit('close')">
                    <i class="bi bi-x-lg"></i>
                </button>
            </div>
            <div class="qi-modal-body">
                <div class="form-group mb-2">
                    <label class="small mb-1">Name</label>
                    <input type="text" class="form-control" v-model="form.title" @input="checkForDuplicate" placeholder="e.g. Premium Cleaning Service">
                    <small v-if="duplicateHint" class="text-danger d-block mt-1">
                        <i class="bi bi-exclamation-triangle mr-1"></i>
                        "{{ duplicateHint.product }}" already exists ({{ formatMoney(duplicateHint.ppu) }}) - it'll be used instead of creating a new item.
                    </small>
                </div>
                <div class="form-row">
                    <div class="col-6 form-group mb-2">
                        <label class="small mb-1">Price (KSh)</label>
                        <input type="number" step="0.01" min="0" class="form-control" v-model.number="form.ppu">
                    </div>
                    <div class="col-6 form-group mb-2">
                        <label class="small mb-1">Tax</label>
                        <select class="form-control" v-model="form.tax_id">
                            <option :value="null">No tax</option>
                            <option v-for="t in taxes" :key="t.id" :value="t.id">{{ t.tax }} ({{ t.tax_perc }}%)</option>
                        </select>
                    </div>
                </div>
                <div class="form-group mb-2">
                    <label class="small mb-1">Buying price (KSh) <span class="text-muted">— optional</span></label>
                    <input type="number" step="0.01" min="0" class="form-control" v-model.number="form.bp" placeholder="What it costs you">
                    <small class="text-muted">Used to work out profit on this item. Leave blank if you don't know it yet — you can set it later in Inventory.</small>
                </div>
                <div class="custom-control custom-checkbox mb-1">
                    <input type="checkbox" class="custom-control-input" id="qi-add-to-pos" v-model="form.addToPos">
                    <label class="custom-control-label small" for="qi-add-to-pos">Add to POS</label>
                </div>
                <small class="text-muted d-block" :class="{ 'mb-2': form.addToPos }">Leave unchecked to keep this as a document-only line item — it won't show up on your Sales page.</small>

                <template v-if="form.addToPos">
                    <div class="custom-control custom-checkbox mb-1">
                        <input type="checkbox" class="custom-control-input" id="qi-is-service" v-model="form.is_Service">
                        <label class="custom-control-label small" for="qi-is-service">This is a service / non-stock item</label>
                    </div>
                    <small class="text-muted">Uncheck if this is a physical product you keep stock of.</small>
                </template>

                <div v-if="error" class="alert alert-danger py-2 px-3 mt-2 mb-0 small">{{ error }}</div>
            </div>
            <div class="qi-modal-footer">
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="$emit('close')">Cancel</button>
                <button type="button" class="btn btn-primary btn-sm" :disabled="!form.title || saving" @click="save">
                    <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
                    Add item
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { apiErrorMessage, formatMoney } from './docHelpers';

export default {
    name: 'ProductQuickAddModal',
    props: {
        initialName: { type: String, default: '' },
    },
    data() {
        return {
            saving: false,
            error: '',
            taxes: [],
            duplicateHint: null,
            duplicateCheckTimer: null,
            form: {
                title: this.initialName,
                ppu: 0,
                bp: null,
                // Defaults checked: most items created from an invoice/quote
                // are genuine reusable catalog additions, not one-off
                // document-only lines - leaving this unchecked by default
                // was why created items kept "disappearing" (can_be_sold
                // false) outside this picker, driving repeat duplicate
                // creation. Still toggleable for the real one-off case.
                addToPos: true,
                is_Service: true,
                tax_id: null,
            },
        };
    },
    created() {
        axios.get('/api/taxes').then(({ data }) => {
            this.taxes = Array.isArray(data) ? data : (data.data || []);
        }).catch(() => {});
        this.checkForDuplicate();
    },
    beforeDestroy() {
        clearTimeout(this.duplicateCheckTimer);
    },
    computed: {
        currency() {
            const u = this.$store.getters.userDetails;
            return (u && u.company && u.company.companysettings && u.company.companysettings.currency) || 'KSh';
        },
    },
    methods: {
        formatMoney(v) { return formatMoney(v, this.currency); },
        // Live heads-up as the cashier types - previews what save() will
        // actually do (reuse, not create) so they're not surprised by the
        // toast after the fact. Reuses the same search endpoint the item
        // picker already calls; no new API needed.
        checkForDuplicate() {
            clearTimeout(this.duplicateCheckTimer);
            const title = this.form.title.trim();
            if (!title) { this.duplicateHint = null; return; }
            this.duplicateCheckTimer = setTimeout(async () => {
                try {
                    const { data } = await axios.get('/api/pos/search', { params: { q: title, sellable_only: 0 } });
                    const products = data.products || [];
                    this.duplicateHint = products.find(p => p.product.trim().toLowerCase() === title.toLowerCase()) || null;
                } catch (e) {
                    this.duplicateHint = null;
                }
            }, 300);
        },
        async save() {
            this.saving = true;
            this.error = '';
            try {
                // Not added to POS -> the service/stock distinction is moot
                // (it will never be sold or deducted from through the POS
                // flow), so it's created as a plain, non-inventory-tracked
                // service under the hood regardless of the (hidden) toggle.
                const isService = this.form.addToPos ? this.form.is_Service : true;
                const { data } = await axios.post('/api/addprod', {
                    title: this.form.title,
                    ppu: this.form.ppu || 0,
                    // Unknown stays 0 (= not set): copying the selling price in here
                    // made every such item look like it sold at zero profit.
                    wholesale_ppu: this.form.bp || 0,
                    wholesale_sp: this.form.ppu || 0,
                    qty: 0,
                    is_Service: isService,
                    affects_profit: true,
                    affects_inventory: this.form.addToPos && !isService,
                    can_be_sold: this.form.addToPos,
                    selectedCategory: null,
                    reuse_if_duplicate: 1,
                });
                const product = data.product;

                if (data.duplicate) {
                    // Reusing an existing product - its real price/tax, not
                    // whatever the cashier had typed into this form, since
                    // those were never saved.
                    this.$toasts.push({ type: 'primary', message: `"${product.product}" already exists - using it instead of creating a duplicate.` });
                    const taxIds = (product.more_details && Array.isArray(product.more_details.taxes)) ? product.more_details.taxes : [];
                    const existingTax = taxIds.length ? (this.taxes.find(t => t.id === taxIds[0]) || null) : null;
                    this.$emit('created', {
                        id: product.id,
                        product: product.product,
                        ppu: Number(product.ppu || 0),
                        is_service: !!product.is_service,
                        tax_id: existingTax ? existingTax.id : null,
                        tax: existingTax,
                    });
                } else {
                    const tax = this.taxes.find(t => t.id === this.form.tax_id) || null;
                    this.$emit('created', {
                        id: product.id,
                        product: product.product,
                        ppu: Number(this.form.ppu || 0),
                        is_service: isService,
                        tax_id: this.form.tax_id,
                        tax,
                    });
                }
            } catch (e) {
                this.error = apiErrorMessage(e, 'Could not create this item. Please try again.');
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
/* A global unscoped rule elsewhere in the app (employee_earning.vue) sets
   `.small { margin: 150px auto }`, which leaks onto every `.small` element
   site-wide. Neutralize it here (higher specificity than the lone class). */
.small { margin: 0; }
.qi-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1080;
    background: rgba(0, 0, 0, .4);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}
.qi-modal-card {
    background: #fff;
    border-radius: 10px;
    width: 100%;
    max-width: 380px;
    box-shadow: 0 12px 40px rgba(0,0,0,.25);
    overflow: hidden;
}
.qi-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid #eee;
}
.qi-modal-body { padding: 16px; }
.qi-modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid #eee;
    background: #fafafa;
}
</style>
